import React, { createContext, useState, useContext, useEffect } from 'react';
import api from './api';
import { unsubscribeFromPush } from './utils/pushNotifications';
import { clearAppBadge } from './utils/badgeService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyToken = async () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');

      if (token && savedUser) {
        try {
          // Verify token is still valid by calling the /auth/me endpoint
          const response = await api.get('/auth/me');
          // Update user with fresh data from server (includes updated roles)
          const freshUser = response.data;
          localStorage.setItem('user', JSON.stringify(freshUser));
          setUser(freshUser);
        } catch (error) {
          // Token is invalid or expired - clear storage and force login
          console.error('Token verification failed:', error.message);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        }
      }
      setLoading(false);
    };

    verifyToken();
  }, []);

  const login = async (username, password) => {
    const response = await api.post('/auth/login', { username, password });
    const { token, user } = response.data;

    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    setUser(user);

    return response.data;
  };

  const logout = async () => {
    // Remove push subscription before clearing auth token (needs auth)
    try {
      await unsubscribeFromPush();
    } catch (e) {
      // Continue with logout even if unsubscribe fails
    }
    // Clear app icon badge
    clearAppBadge();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const updateUser = (updatedUser) => {
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setUser(updatedUser);
  };

  const hasRole = (...requiredRoles) => {
    if (!user) return false;
    // Support multi-role: check if user has ANY of the required roles
    const userRoles = user.roles || [user.role];
    return requiredRoles.some(r => userRoles.includes(r));
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, hasRole, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
